import { useState, useRef, useEffect, useMemo, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useAuth } from '../../shared/auth/AuthContext';
import { uuidv4 } from '../../shared/utils/uuid';
import { queryKeys } from '../../shared/api/query-keys';
import { PwaInstallPrompt } from './components/PwaInstallPrompt';
import { fetchPosCatalog } from './posCatalog';
import { formatCurrency } from '../../shared/utils/format';

/* ── Types ── */

interface Product {
  id: string;
  code: string;
  name: string;
  salePrice: number;
  unit: string;
  stock: number;
}

interface CartItem {
  product: Product;
  quantity: number;
  total: number;
}

type PaymentMethod = 'CASH' | 'CREDIT_CARD' | 'DEBIT_CARD' | 'PIX';

const PAYMENT_METHODS: ReadonlyArray<{ id: PaymentMethod; label: string; icon: string }> = [
  { id: 'CASH', label: 'Dinheiro', icon: 'payments' },
  { id: 'CREDIT_CARD', label: 'Crédito', icon: 'credit_card' },
  { id: 'DEBIT_CARD', label: 'Débito', icon: 'credit_card' },
  { id: 'PIX', label: 'PIX', icon: 'qr_code_2' },
];

/* ── Helpers ── */

function formatQuantity(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/* ── Pagina ── */

export function PosSalePage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [term, setTerm] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [lastAdded, setLastAdded] = useState<CartItem | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('CASH');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const {
    data: products,
    isLoading: isCatalogLoading,
    isError: isCatalogError,
    refetch: refetchCatalog,
  } = useQuery({
    queryKey: [...queryKeys.pos.products(tenantId), term],
    // Contrato real: GET /products + GET /inventory/balance (sem /tenants/:id/pos/products)
    queryFn: async () => fetchPosCatalog(term),
    enabled: !!tenantId,
  });

  /* ── Carrinho ── */

  const addToCart = (product: Product): void => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      const next = existing
        ? prev.map((item) =>
            item.product.id === product.id
              ? {
                  ...item,
                  quantity: item.quantity + 1,
                  total: (item.quantity + 1) * product.salePrice,
                }
              : item,
          )
        : [...prev, { product, quantity: 1, total: product.salePrice }];

      const added = next.find((item) => item.product.id === product.id);
      if (added) setLastAdded(added);
      return next;
    });
  };

  const updateQuantity = (productId: string, quantity: number): void => {
    if (quantity <= 0) {
      setCart((prev) => prev.filter((item) => item.product.id !== productId));
      return;
    }

    setCart((prev) =>
      prev.map((item) =>
        item.product.id === productId
          ? { ...item, quantity, total: quantity * item.product.salePrice }
          : item,
      ),
    );
  };

  const removeFromCart = (productId: string): void => {
    setCart((prev) => prev.filter((item) => item.product.id !== productId));
    setLastAdded((prev) => (prev?.product.id === productId ? null : prev));
  };

  const resetSale = (): void => {
    setCart([]);
    setLastAdded(null);
    setTerm('');
    setMessage(null);
    searchInputRef.current?.focus();
  };

  const subtotal = cart.reduce((sum, item) => sum + item.total, 0);
  const discount = 0; // Sem regra de desconto no fluxo atual do backend.
  const cartTotal = subtotal - discount;

  /* ── Busca / bipagem ── */

  const matches = useMemo(() => {
    if (!term.trim() || !products) return [];
    return products.slice(0, 8);
  }, [term, products]);

  const submitTerm = (): void => {
    const query = term.trim();
    if (!query || !products) return;

    const exact = products.find((p) => p.code === query);
    const chosen = exact ?? (products.length === 1 ? products[0] : undefined);

    if (chosen) {
      addToCart(chosen);
      setTerm('');
      setMessage(null);
      return;
    }

    if (products.length === 0) {
      setMessage({ type: 'error', text: `Nenhum produto encontrado para "${query}".` });
    }
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitTerm();
    }
    if (e.key === 'Escape') {
      setTerm('');
    }
  };

  const pickFromResults = (product: Product): void => {
    addToCart(product);
    setTerm('');
    setMessage(null);
    searchInputRef.current?.focus();
  };

  /* ── Finalizacao ── */

  const completeSale = async (): Promise<void> => {
    if (cart.length === 0 || isSubmitting) return;

    setIsSubmitting(true);
    setMessage(null);

    try {
      // Fluxo stateful real: terminal da sessao aberta -> start -> items -> payment -> complete
      const { getOpenSessionTerminalId, submitPosSaleOnline } = await import('./posSaleFlow');
      const terminalId = await getOpenSessionTerminalId();
      await submitPosSaleOnline({
        terminalId,
        items: cart.map((item) => ({
          productId: item.product.id,
          name: item.product.name,
          sku: item.product.code,
          quantity: item.quantity,
          unitValue: item.product.salePrice,
        })),
        payments: [{ method: paymentMethod, value: cartTotal }],
        offlineOperationId: uuidv4(),
      });

      setMessage({ type: 'success', text: 'Venda finalizada com sucesso.' });
      setCart([]);
      setLastAdded(null);
      searchInputRef.current?.focus();
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Erro ao finalizar venda: ${(error as Error).message}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ── Teclas de funcao (F1..F4) ── */

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'F2') {
        event.preventDefault();
        resetSale();
      }
      if (event.key === 'F4') {
        event.preventDefault();
        if (cart.length > 0) resetSale();
      }
      if (event.key === 'F3') {
        event.preventDefault();
        void completeSale();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  });

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  /* ── Render ── */

  const fnKeyBase =
    'rounded-md py-sm px-md flex items-center justify-between font-label-md text-label-md font-bold transition-colors';

  return (
    <>
      <main className="flex-1 flex flex-col bg-background p-lg overflow-hidden gap-lg">
          {/* Busca + ultimo item */}
          <div className="flex gap-lg shrink-0">
            <div className="flex-1 bg-surface-container-lowest border border-outline-variant rounded-xl p-md shadow-sm flex flex-col justify-center">
              <label
                className="font-label-md text-label-md text-on-surface-variant mb-xs flex items-center gap-xs"
                htmlFor="product-search"
              >
                <span className="material-symbols-outlined text-[16px]" aria-hidden="true">
                  barcode_scanner
                </span>
                Código ou Nome do Produto
              </label>

              <div className="relative">
                <span
                  className="material-symbols-outlined absolute left-sm top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none"
                  aria-hidden="true"
                >
                  search
                </span>
                <input
                  id="product-search"
                  ref={searchInputRef}
                  type="text"
                  autoFocus
                  autoComplete="off"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder="Digite ou bipe..."
                  className="w-full pl-xl pr-md py-sm bg-surface-container-low border-none rounded-md font-headline-sm text-headline-sm text-on-surface focus:ring-2 focus:ring-primary focus:bg-surface-container-lowest outline-none transition-all"
                />

                {/* Resultados: so aparecem enquanto ha busca ativa */}
                {term.trim() !== '' && (
                  <div className="absolute left-0 right-0 top-full mt-xs z-30 bg-surface-container-lowest border border-outline-variant rounded-xl shadow-lg overflow-hidden max-h-80 overflow-y-auto">
                    {isCatalogLoading && (
                      <p className="px-md py-sm font-body-sm text-body-sm text-on-surface-variant">
                        Buscando produtos...
                      </p>
                    )}

                    {isCatalogError && (
                      <div className="px-md py-sm flex items-center justify-between gap-sm">
                        <span className="font-body-sm text-body-sm text-error">
                          Falha ao carregar o catalogo.
                        </span>
                        <button
                          type="button"
                          onClick={() => void refetchCatalog()}
                          className="font-label-md text-label-md font-bold text-primary hover:underline"
                        >
                          Tentar de novo
                        </button>
                      </div>
                    )}

                    {!isCatalogLoading && !isCatalogError && matches.length === 0 && (
                      <p className="px-md py-sm font-body-sm text-body-sm text-on-surface-variant">
                        Nenhum produto encontrado.
                      </p>
                    )}

                    {matches.map((product) => (
                      <button
                        key={product.id}
                        type="button"
                        onClick={() => pickFromResults(product)}
                        className="w-full text-left px-md py-sm flex items-center gap-md border-b border-surface-container-high last:border-b-0 hover:bg-surface-container-low transition-colors"
                      >
                        <span className="flex-1 min-w-0">
                          <span className="block font-body-md text-body-md font-bold text-on-surface truncate">
                            {product.name}
                          </span>
                          <span className="block font-label-sm text-label-sm text-on-surface-variant">
                            COD: {product.code} · Estoque: {formatQuantity(product.stock)}{' '}
                            {product.unit}
                          </span>
                        </span>
                        <span className="font-body-md text-body-md font-bold text-primary shrink-0">
                          {formatCurrency(product.salePrice)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="w-1/3 bg-surface-container-lowest border border-outline-variant rounded-xl p-md shadow-sm flex items-center gap-md">
              <div className="w-16 h-16 bg-surface-container-low rounded-md border border-outline-variant flex items-center justify-center shrink-0">
                <span
                  className="material-symbols-outlined text-[32px] text-on-surface-variant"
                  aria-hidden="true"
                >
                  image
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-label-md text-label-md text-on-surface-variant truncate">
                  Último Item Adicionado
                </div>
                <div className="font-headline-sm text-headline-sm font-bold text-on-surface truncate">
                  {lastAdded ? lastAdded.product.name : '--'}
                </div>
                <div className="font-body-sm text-body-sm text-on-surface-variant">
                  {lastAdded
                    ? `${formatQuantity(lastAdded.quantity)} x ${formatCurrency(lastAdded.product.salePrice)}`
                    : 'Nenhum item bipado'}
                </div>
              </div>
              <div className="font-headline-md text-headline-md font-bold text-primary shrink-0">
                {lastAdded ? formatCurrency(lastAdded.total) : formatCurrency(0)}
              </div>
            </div>
          </div>

          {/* Lista + totais */}
          <div className="flex flex-1 gap-lg min-h-0">
            <div className="flex-1 bg-surface-container-lowest border border-outline-variant rounded-xl flex flex-col shadow-sm overflow-hidden">
              <div className="bg-surface-container-low border-b border-outline-variant px-md py-sm flex font-label-md text-label-md text-on-surface-variant font-bold sticky top-0">
                <div className="w-12 text-center">#</div>
                <div className="flex-1">Produto</div>
                <div className="w-24 text-right">Qtd</div>
                <div className="w-32 text-right">V. Unit</div>
                <div className="w-32 text-right pr-md">Subtotal</div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {cart.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center gap-sm p-xl text-center">
                    <span
                      className="material-symbols-outlined text-[48px] text-outline"
                      aria-hidden="true"
                    >
                      barcode_scanner
                    </span>
                    <p className="font-body-md text-body-md text-on-surface-variant">
                      Nenhum item na venda.
                    </p>
                    <p className="font-label-md text-label-md text-on-surface-variant">
                      Bipe um código ou digite o nome do produto para começar.
                    </p>
                  </div>
                ) : (
                  cart.map((item, index) => (
                    <div
                      key={item.product.id}
                      className={`group flex px-md py-sm border-b border-surface-container-high hover:bg-surface-container-low transition-colors items-center font-body-md text-body-md text-on-surface${
                        index % 2 === 1 ? ' bg-surface-bright' : ''
                      }`}
                    >
                      <div className="w-12 text-center font-label-sm text-label-sm text-on-surface-variant">
                        {String(index + 1).padStart(3, '0')}
                      </div>

                      <div className="flex-1 flex flex-col min-w-0">
                        <span className="font-bold truncate">{item.product.name}</span>
                        <span className="font-label-sm text-label-sm text-on-surface-variant">
                          COD: {item.product.code}
                        </span>
                      </div>

                      <div className="w-24 text-right flex items-center justify-end gap-xs">
                        {/* Controles aparecem no hover/foco: em repouso a linha e identica ao mock */}
                        <span className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center gap-xs">
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                            className="w-5 h-5 rounded border border-outline-variant text-on-surface-variant hover:bg-surface-container-high flex items-center justify-center leading-none"
                            aria-label={`Diminuir quantidade de ${item.product.name}`}
                          >
                            <span
                              className="material-symbols-outlined text-[14px]"
                              aria-hidden="true"
                            >
                              remove
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                            className="w-5 h-5 rounded border border-outline-variant text-on-surface-variant hover:bg-surface-container-high flex items-center justify-center leading-none"
                            aria-label={`Aumentar quantidade de ${item.product.name}`}
                          >
                            <span
                              className="material-symbols-outlined text-[14px]"
                              aria-hidden="true"
                            >
                              add
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => removeFromCart(item.product.id)}
                            className="w-5 h-5 rounded border border-error/30 text-error hover:bg-error-container flex items-center justify-center leading-none"
                            aria-label={`Remover ${item.product.name}`}
                          >
                            <span
                              className="material-symbols-outlined text-[14px]"
                              aria-hidden="true"
                            >
                              close
                            </span>
                          </button>
                        </span>
                        {formatQuantity(item.quantity)}
                      </div>

                      <div className="w-32 text-right text-on-surface-variant">
                        {formatCurrency(item.product.salePrice)}
                      </div>
                      <div className="w-32 text-right pr-md font-bold">
                        {formatCurrency(item.total)}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="w-context-drawer flex flex-col gap-md shrink-0">
              {/* Totais */}
              <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-md shadow-sm flex flex-col gap-sm">
                <div className="flex justify-between items-center font-body-md text-body-md text-on-surface-variant">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between items-center font-body-md text-body-md text-on-surface-variant">
                  <span>Descontos</span>
                  <span className={discount > 0 ? 'text-error' : undefined}>
                    {discount > 0 ? `- ${formatCurrency(discount)}` : formatCurrency(0)}
                  </span>
                </div>
                <div className="border-t border-outline-variant my-xs" />
                <div className="flex justify-between items-end">
                  <span className="font-headline-sm text-headline-sm font-bold text-on-surface">
                    Total a Pagar
                  </span>
                  <span className="font-display-lg text-display-lg text-primary tracking-tight">
                    {formatCurrency(cartTotal)}
                  </span>
                </div>
              </div>

              {/* Mensagem de resultado */}
              {message && (
                <div
                  role="alert"
                  className={`rounded border px-md py-sm font-body-md text-body-md flex items-start gap-sm ${
                    message.type === 'success'
                      ? 'bg-secondary-container border-secondary text-on-secondary-container'
                      : 'bg-error-container border-error/30 text-on-error-container'
                  }`}
                >
                  <span className="material-symbols-outlined text-[18px]" aria-hidden="true">
                    {message.type === 'success' ? 'check_circle' : 'error'}
                  </span>
                  <span className="flex-1">{message.text}</span>
                </div>
              )}

              {/* Formas de pagamento */}
              <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-sm shadow-sm grid grid-cols-2 gap-sm">
                {PAYMENT_METHODS.map((method) => {
                  const isSelected = paymentMethod === method.id;
                  return (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => setPaymentMethod(method.id)}
                      aria-pressed={isSelected}
                      className={`rounded-md p-md flex flex-col items-center justify-center gap-xs transition-colors group border ${
                        isSelected
                          ? 'bg-secondary-container hover:bg-secondary-fixed border-secondary'
                          : 'bg-surface-container-low hover:bg-surface-container-high border-outline-variant'
                      }`}
                    >
                      <span
                        className={`material-symbols-outlined text-[32px] group-hover:text-primary transition-colors ${
                          isSelected ? 'text-on-secondary-container' : 'text-on-surface-variant'
                        }`}
                        aria-hidden="true"
                      >
                        {method.icon}
                      </span>
                      <span
                        className={`font-label-md text-label-md font-bold ${
                          isSelected ? 'text-on-secondary-container' : 'text-on-surface'
                        }`}
                      >
                        {method.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Teclas de funcao */}
              <div className="grid grid-cols-2 gap-sm mt-auto">
                <Link
                  to="/support/tickets"
                  className={`${fnKeyBase} bg-surface-container-low hover:bg-surface-container-high border border-outline-variant text-on-surface`}
                >
                  <span className="bg-surface-container-highest px-xs rounded text-on-surface-variant">
                    F1
                  </span>
                  Ajuda
                </Link>

                <button
                  type="button"
                  onClick={resetSale}
                  className={`${fnKeyBase} bg-surface-container-low hover:bg-surface-container-high border border-outline-variant text-on-surface`}
                >
                  <span className="bg-surface-container-highest px-xs rounded text-on-surface-variant">
                    F2
                  </span>
                  Nova
                </button>

                <button
                  type="button"
                  onClick={resetSale}
                  disabled={cart.length === 0}
                  className={`${fnKeyBase} bg-error-container hover:bg-error/20 border border-error/30 text-on-error-container disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  <span className="bg-error/10 px-xs rounded">F4</span>
                  Cancelar
                </button>

                <button
                  type="button"
                  onClick={() => void completeSale()}
                  disabled={cart.length === 0 || isSubmitting}
                  className={`${fnKeyBase} bg-primary hover:bg-primary-container text-on-primary shadow-sm disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  <span className="bg-on-primary/20 px-xs rounded">F3</span>
                  {isSubmitting ? 'Enviando...' : 'Finalizar'}
                </button>
              </div>
            </div>
          </div>
        </main>

      <PwaInstallPrompt />
    </>
  );
}
